type MediaInputManagerOptions = {
  onAudioChange: (deviceId: string) => Promise<void> | void;
  onVideoChange: (deviceId: string) => Promise<void> | void;
};

export class MediaInputManager {
  audioDevices: MediaDeviceInfo[] = [];
  videoDevices: MediaDeviceInfo[] = [];
  currentAudioDeviceId: string | null = null;
  currentVideoDeviceId: string | null = null;
  static listener: (() => void) | null = null;
  private readonly onAudioChange: (deviceId: string) => Promise<void> | void;
  private readonly onVideoChange: (deviceId: string) => Promise<void> | void;

  constructor(options: MediaInputManagerOptions) {
    this.onAudioChange = options.onAudioChange;
    this.onVideoChange = options.onVideoChange;
  }

  async init() {
    await this.updateDeviceLists();
    navigator.mediaDevices.ondevicechange = () => {
      this.handleDeviceChange();
    };
  }

  private async updateDeviceLists() {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const audioInputs = devices.filter(
      (d) => d.kind === 'audioinput' && d.deviceId !== 'default'
    );
    const videoInputs = devices.filter(
      (d) => d.kind === 'videoinput' && d.deviceId !== 'default'
    );

    const prevAudioIds = this.audioDevices.map((d) => d.deviceId);
    const prevVideoIds = this.videoDevices.map((d) => d.deviceId);

    this.audioDevices = audioInputs;
    this.videoDevices = videoInputs;

    const addedAudioBT = audioInputs.find(
      (d) => !prevAudioIds.includes(d.deviceId) && this.isBluetoothDevice(d.label)
    );
    const currentAudioStillExists = audioInputs.some(
      (d) => d.deviceId === this.currentAudioDeviceId
    );

    if (addedAudioBT || !currentAudioStillExists) {
      const pick = addedAudioBT || audioInputs[0];
      if (pick && pick.deviceId !== this.currentAudioDeviceId) {
        this.currentAudioDeviceId = pick.deviceId;
        await this.onAudioChange(pick.deviceId);
      }
    }

    const addedVideo = videoInputs.find(
      (d) => !prevVideoIds.includes(d.deviceId)
    );
    const currentVideoStillExists = videoInputs.some(
      (d) => d.deviceId === this.currentVideoDeviceId
    );

    if (addedVideo || !currentVideoStillExists) {
      const pick = addedVideo || videoInputs[0];
      if (pick && pick.deviceId !== this.currentVideoDeviceId) {
        this.currentVideoDeviceId = pick.deviceId;
        await this.onVideoChange(pick.deviceId);
      }
    }
  }

  private async handleDeviceChange() {
    try {
      await this.updateDeviceLists();
      MediaInputManager.listener?.();
    } catch (err) {
      console.error('Error updating media devices:', err);
    }
  }

  private isBluetoothDevice(label: string): boolean {
    const name = label.toLowerCase();
    return name.includes('bluetooth') || name.includes('airpods') || name.includes('headset');
  }

  setAudioDeviceId(deviceId: string) {
    this.currentAudioDeviceId = deviceId;
  }

  setVideoDeviceId(deviceId: string) {
    this.currentVideoDeviceId = deviceId;
  }

  static onDeviceChange(listener: () => void) {
    MediaInputManager.listener = listener;
  }
}
